import { classify, errorView, newIdempotencyKey, orgSlugProblem, slugify } from "@nodus/api";
import { Button, Field, Input, UncertainOutcome } from "@nodus/ui";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import * as React from "react";
import { AuthCard } from "../../../app/auth-card";
import { useRuntime } from "../../../app/runtime";
import { rememberScope } from "../../../app/scope";
import { InviteCard, JoinOrgCard } from "../../../features/console/invite-card";
import { useAuth } from "../../../auth/auth-context";

// Onboarding (ADR-081, ui-spec §6.19): a pending invite comes first, then an org at the user's verified company domain
// (ADR-128); otherwise the explicit create-org step. Nothing
// creates an org silently in the browser, so an invitee never spends their starter grant on a stray org.
export const Route = createFileRoute("/_authed/onboarding")({
  validateSearch: (s: Record<string, unknown>): { new?: string; card?: string; destination?: "reserved-compute" } => ({
    ...(s.card === "complete" ? { card: "complete" } : {}),
    ...(typeof s.new === "string" ? { new: s.new } : {}),
    ...((s.destination === "marketplace" || s.destination === "reserved-compute") ? { destination: "reserved-compute" as const } : {}),
  }),
  component: Onboarding,
});

function Onboarding() {
  const search = Route.useSearch();
  const { api } = useRuntime();
  const { session } = useAuth();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const invites = useQuery({ queryKey: ["user", "invites"], queryFn: api.invites });
  const [skipInvites, setSkipInvites] = React.useState(search.new === "1");
  const joinable = useQuery({ queryKey: ["user", "joinable-orgs"], queryFn: api.joinableOrgs, enabled: search.new !== "1" });
  const [skipJoin, setSkipJoin] = React.useState(false);
  const [joinError, setJoinError] = React.useState<string>();

  async function open(org: string, project = "default") {
    await qc.invalidateQueries({ queryKey: ["user"] });
    if (session) rememberScope(session.user.id, org, project);
    if (search.destination === "reserved-compute") void navigate({ href: "/reserved-compute/request" });
    else void navigate({ to: "/$org", params: { org } });
  }

  const first = invites.data?.[0];
  const join = joinable.data?.[0];
  if (invites.isLoading || joinable.isLoading) return <AuthCard title="Setting things up…" />;
  if (first && !skipInvites) {
    return (
      <InviteCard
        invite={first}
        onSkip={() => setSkipInvites(true)}
        onAccept={async () => {
          const r = await api.acceptInvite({ invite: first.uid });
          await open(r.org, first.projects?.[0] ?? "default");
        }}
      />
    );
  }
  if (join && !skipJoin) {
    return (
      <JoinOrgCard
        org={join}
        error={joinError}
        onSkip={() => setSkipJoin(true)}
        onJoin={async () => {
          setJoinError(undefined);
          try {
            await open((await api.joinOrg(join.name)).org);
          } catch (err) {
            const v = errorView(err);
            setJoinError(v.reason === "QuotaExceeded" ? `${join.displayName} has no free seats. Ask an Owner or Admin to add one, then try again.` : v.title);
          }
        }}
      />
    );
  }
  return <CreateOrg />;
}

function CreateOrg() {
  const search = Route.useSearch();
  const { api } = useRuntime();
  const { session } = useAuth();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const suggested = session?.user.name ? `${session.user.name}'s team` : "My team";
  const draftKey = `signup-org:${session?.user.id ?? ""}`;
  const [draft] = React.useState(() => {
    try { return JSON.parse(sessionStorage.getItem(draftKey) ?? "null") as { display?: string; slug?: string; key?: string } | null; }
    catch { return null; }
  });
  const [display, setDisplay] = React.useState(typeof draft?.display === "string" ? draft.display : suggested);
  const [slug, setSlug] = React.useState(typeof draft?.slug === "string" ? draft.slug : slugify(suggested));
  const [slugEdited, setSlugEdited] = React.useState(Boolean(draft?.slug));
  const [key] = React.useState(() => typeof draft?.key === "string" ? draft.key : newIdempotencyKey());
  const [error, setError] = React.useState<string>();
  const [uncertain, setUncertain] = React.useState(false);
  const [busy, setBusy] = React.useState(false);
  const problem = orgSlugProblem(slug);

  async function done(org: string) {
    sessionStorage.removeItem(draftKey);
    await qc.invalidateQueries({ queryKey: ["user"] });
    if (session) rememberScope(session.user.id, org, "default");
    void navigate({ href: `/${encodeURIComponent(org)}/billing/overview${search.destination ? "?destination=reserved-compute" : ""}` });
  }

  async function verifyCard(restart = false) {
    setBusy(true);
    setError(undefined);
    try {
      sessionStorage.setItem(draftKey, JSON.stringify({ display, slug, key }));
      const returnURL = new URL("/onboarding", window.location.origin);
      returnURL.searchParams.set("new", "1");
      if (search.destination) returnURL.searchParams.set("destination", search.destination);
      const checkout = await api.signupCard(returnURL.toString(), restart);
      window.location.assign(checkout.url);
    } catch (err) {
      setError(errorView(err).title);
      setBusy(false);
    }
  }

  async function submit() {
    setBusy(true);
    setError(undefined);
    setUncertain(false);
    try {
      const created = await api.createOrganization(slug, display.trim() || slug, key);
      await done(created.metadata.name);
    } catch (err) {
      const c = classify(err, true);
      if (c === "uncertain") setUncertain(true);
      else setError(err instanceof Error ? err.message : "Could not create the organization.");
    } finally {
      setBusy(false);
    }
  }

  async function check() {
    const orgs = await api.organizations().catch(() => []);
    if (orgs.some((o) => o.metadata.name === slug)) await done(slug);
    else setUncertain(false);
  }

  return (
    <AuthCard title="Create your organization" description="Verify your card before creating your organization. No payment is collected during verification. Each card can be used by one account.">
      <form
        className="grid gap-4"
        onSubmit={(e) => {
          e.preventDefault();
          if (search.card === "complete") void submit();
          else void verifyCard();
        }}
      >
        <Field id="org-name" label="Organization name" required>
          <Input
            value={display}
            onChange={(e) => {
              setDisplay(e.target.value);
              if (!slugEdited) setSlug(slugify(e.target.value));
            }}
          />
        </Field>
        <Field id="org-slug" label="URL name" hint={<span className="font-mono">console…/{slug || "…"}/default</span>} error={slug ? problem : undefined} required>
          <Input
            value={slug}
            className="font-mono"
            onChange={(e) => {
              setSlugEdited(true);
              setSlug(e.target.value.toLowerCase());
            }}
          />
        </Field>
        {error ? <p role="alert" className="text-ui text-danger-fg">{error}</p> : null}
        {uncertain ? <UncertainOutcome idempotencyKey={key} onCheck={() => void check()} onRetry={() => void submit()} /> : null}
        <Button type="submit" disabled={busy || Boolean(problem)}>{search.card === "complete" ? "Create organization" : "Verify card to continue"}</Button>
        {search.card === "complete" ? <Button type="button" disabled={busy} onClick={() => void verifyCard(true)}>Verify another card</Button> : null}
      </form>
    </AuthCard>
  );
}
